import React, { useMemo, useState } from 'react';
import { Box, Typography, Tooltip, alpha, MenuItem, Select } from '@mui/material';

const ACCENT = '#137274';

const LEVELS = [
  { max: 0, dark: 'rgba(255,255,255,0.06)', light: 'rgba(15,60,70,0.08)' },
  { max: 1, dark: alpha(ACCENT, 0.28), light: alpha(ACCENT, 0.22) },
  { max: 3, dark: alpha(ACCENT, 0.48), light: alpha(ACCENT, 0.4) },
  { max: 6, dark: alpha(ACCENT, 0.72), light: alpha(ACCENT, 0.62) },
  { max: Infinity, dark: ACCENT, light: '#0f5c5e' },
];

const levelFor = (count, isDark) => {
  const tier = LEVELS.find((l) => count <= l.max) || LEVELS[LEVELS.length - 1];
  return isDark ? tier.dark : tier.light;
};

const toUtcKey = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * LeetCode-style contribution heatmap for Accepted submissions.
 */
const SolveActivityHeatmap = ({
  activity,
  isDark,
  border,
  muted,
  surface,
  year,
  availableYears,
  onYearChange,
  loading,
}) => {
  const [hover, setHover] = useState(null);

  const { weeks, monthLabels, activeDays, totalSubmissions } = useMemo(() => {
    const days = activity?.days || [];
    const map = {};
    days.forEach((d) => {
      map[d.date] = d.count;
    });

    let start;
    let end;
    if (year) {
      // Full calendar year, Sunday-aligned like GitHub/LeetCode's past-year view.
      start = new Date(Date.UTC(year, 0, 1));
      start.setUTCDate(start.getUTCDate() - start.getUTCDay());
      end = new Date(Date.UTC(year, 11, 31));
    } else {
      const today = new Date();
      end = new Date(
        Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
      );
      // ~53 weeks back, aligned to Sunday (UTC)
      start = new Date(end);
      start.setUTCDate(start.getUTCDate() - 52 * 7);
      start.setUTCDate(start.getUTCDate() - start.getUTCDay());
    }

    const cells = [];
    for (let t = start.getTime(); t <= end.getTime(); t += 86400000) {
      const d = new Date(t);
      const key = toUtcKey(d);
      cells.push({
        date: key,
        count: map[key] || 0,
        dow: d.getUTCDay(),
        month: d.getUTCMonth(),
        year: d.getUTCFullYear(),
      });
    }

    const weekCols = [];
    for (let i = 0; i < cells.length; i += 7) {
      weekCols.push(cells.slice(i, i + 7));
    }

    const labels = [];
    let lastMonth = -1;
    weekCols.forEach((week, wi) => {
      const first = week[0];
      if (!first) return;
      // In year mode the grid is padded back to the nearest Sunday, so the
      // first column or two can be spillover from December of the prior
      // year — skip labeling those, they'd otherwise sit right on top of
      // the real "Jan" label a column or two later.
      if (year && first.year !== year) return;
      if (first.month !== lastMonth) {
        labels.push({ index: wi, label: MONTHS[first.month] });
        lastMonth = first.month;
      }
    });

    return {
      weeks: weekCols,
      monthLabels: labels,
      activeDays: activity?.activeDays ?? days.length,
      totalSubmissions:
        activity?.totalSubmissions ??
        days.reduce((s, d) => s + (d.count || 0), 0),
    };
  }, [activity, year]);

  const cell = 11;
  const gap = 3;

  return (
    <Box
      sx={{
        p: { xs: 1.75, sm: 2.25 },
        borderRadius: 2.5,
        border: `1px solid ${border}`,
        bgcolor: surface,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.75,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
          {activeDays}{' '}
          <Box component="span" sx={{ fontWeight: 600, color: muted }}>
            active days
          </Box>{' '}
          {year ? `in ${year}` : 'in the last year'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="caption" sx={{ color: muted, fontWeight: 700 }}>
            {totalSubmissions} accepted submission{totalSubmissions === 1 ? '' : 's'}
          </Typography>
          {Array.isArray(availableYears) && availableYears.length > 0 && (
            <Select
              size="small"
              value={year || 'current'}
              onChange={(e) => {
                const v = e.target.value;
                onYearChange?.(v === 'current' ? null : Number(v));
              }}
              sx={{
                fontSize: '0.78rem',
                fontWeight: 700,
                height: 30,
                color: muted,
                '.MuiOutlinedInput-notchedOutline': { borderColor: border },
                '.MuiSvgIcon-root': { color: muted },
              }}
            >
              <MenuItem value="current" sx={{ fontSize: '0.82rem' }}>
                Current
              </MenuItem>
              {availableYears.map((y) => (
                <MenuItem key={y} value={y} sx={{ fontSize: '0.82rem' }}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          overflowX: 'auto',
          pb: 0.5,
          WebkitOverflowScrolling: 'touch',
          opacity: loading ? 0.5 : 1,
          transition: 'opacity 0.15s ease',
        }}
      >
        <Box sx={{ minWidth: weeks.length * (cell + gap) + 28 }}>
          {/* Month labels */}
          <Box
            sx={{
              display: 'flex',
              ml: '28px',
              mb: 0.5,
              height: 14,
              position: 'relative',
            }}
          >
            {monthLabels.map((m) => (
              <Typography
                key={`${m.label}-${m.index}`}
                variant="caption"
                sx={{
                  position: 'absolute',
                  left: m.index * (cell + gap),
                  fontSize: '0.65rem',
                  color: muted,
                  fontWeight: 700,
                }}
              >
                {m.label}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: `${gap}px` }}>
            {/* Weekday gutter */}
            <Box
              sx={{
                width: 22,
                display: 'flex',
                flexDirection: 'column',
                gap: `${gap}px`,
                flexShrink: 0,
                pt: 0,
              }}
            >
              {['', 'Mon', '', 'Wed', '', 'Fri', ''].map((label, i) => (
                <Box
                  key={i}
                  sx={{
                    height: cell,
                    fontSize: '0.58rem',
                    color: muted,
                    fontWeight: 700,
                    lineHeight: `${cell}px`,
                    textAlign: 'right',
                    pr: 0.5,
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>

            {weeks.map((week, wi) => (
              <Box
                key={wi}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: `${gap}px`,
                }}
              >
                {week.map((day) => (
                  <Tooltip
                    key={day.date}
                    arrow
                    title={
                      day.count
                        ? `${day.count} accepted on ${day.date}`
                        : `No solves on ${day.date}`
                    }
                  >
                    <Box
                      onMouseEnter={() => setHover(day.date)}
                      onMouseLeave={() => setHover(null)}
                      sx={{
                        width: cell,
                        height: cell,
                        borderRadius: 0.5,
                        bgcolor: levelFor(day.count, isDark),
                        outline:
                          hover === day.date
                            ? `1px solid ${isDark ? '#e6edf3' : '#0f2a2b'}`
                            : `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,60,70,0.06)'}`,
                        cursor: 'default',
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
          mt: 1.5,
        }}
      >
        <Typography variant="caption" sx={{ color: muted, fontWeight: 600, mr: 0.5 }}>
          Less
        </Typography>
        {LEVELS.map((l, i) => (
          <Box
            key={i}
            sx={{
              width: cell,
              height: cell,
              borderRadius: 0.5,
              bgcolor: isDark ? l.dark : l.light,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,60,70,0.06)'}`,
            }}
          />
        ))}
        <Typography variant="caption" sx={{ color: muted, fontWeight: 600, ml: 0.5 }}>
          More
        </Typography>
      </Box>
    </Box>
  );
};

export default SolveActivityHeatmap;
